import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, type ApiResponse } from "@/api/client";
import { useAdminDestinationList } from "@/hooks/useAdminLists";
import { useSearchParams } from "react-router-dom";
import {
  LEAD_STATUS_LABELS,
  type LeadKind,
  type User,
  LEAD_STATUSES,
  LEAD_KINDS,
} from "@ckgo2/shared/types";
import { formatPrice } from "@ckgo2/shared/utils";
import { formatDate } from "@/lib/format";
import { LEAD_STATUS_CONFIG } from "@/lib/statusConfig";
import { enumLabel } from "@/i18n/enumLabels";
import { LEAD_KIND_LABEL, LeadKindBadge } from "./leadKind";
import FacetedListLayout from "@/components/common/FacetedListLayout";
import FacetPanel, { type FacetBucket } from "@/components/common/FacetPanel";
import { Plus, LayoutGrid, UserCircle } from "lucide-react";

interface LeadRow {
  id: string;
  leadNumber: string;
  status: keyof typeof LEAD_STATUS_LABELS;
  kind: LeadKind;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  destinationId?: string | null;
  assignedTo?: string | null;
  paxAdults: number;
  paxChildren: number;
  estimatedValue?: number | null;
  currency: string;
  leadScore: number;
  createdAt: string;
  assignedToName?: string | null;
  destinationName?: string | null;
  tripTitle?: string | null;
}

interface FacetedLeads {
  data: LeadRow[];
  total: number;
  page: number;
  limit: number;
  facets: Record<string, FacetBucket[]>;
  success: true;
}

export default function LeadsPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["leads", qs],
    queryFn: () => api.get<FacetedLeads>(`/api/admin/leads?${qs}`),
  });

  // Lookup lists for facet/row labels (ids → names).
  const { data: usersData } = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get<ApiResponse<User[]>>("/api/admin/users"),
  });
  const { data: destData } = useAdminDestinationList();

  const userName = (id: string) => usersData?.data.find((u) => u.id === id)?.name ?? id;
  const destName = (id: string) => destData?.data.find((d) => d.id === id)?.name ?? id;

  const leads = data?.data ?? [];
  const facets = data?.facets ?? {};
  const total = data?.total ?? 0;
  const limit = data?.limit ?? 25;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("crm.leads.title")}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">{t("crm.leads.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/leads/new" className="btn-primary">
            <Plus className="h-5 w-5" />
            {t("crm.leads.newLead")}
          </Link>
          <Link to="/leads/kanban" className="btn-ghost">
            <LayoutGrid className="h-5 w-5" />
            {t("crm.leads.kanban")}
          </Link>
        </div>
      </div>

      <FacetedListLayout
        searchPlaceholder={t("crm.leads.searchPlaceholder")}
        total={total}
        limit={limit}
        isLoading={isLoading}
        isFetching={isFetching}
        filters={
          <>
            <FacetPanel
              title={t("common.fields.status")}
              paramKey="status"
              order={{ enum: LEAD_STATUSES }}
              buckets={facets.status ?? []}
              labelFor={(v) => enumLabel(LEAD_STATUS_LABELS, v as keyof typeof LEAD_STATUS_LABELS)}
            />
            <FacetPanel
              title={t("crm.leads.kindFacet")}
              paramKey="kind"
              order={{ enum: LEAD_KINDS }}
              buckets={facets.kind ?? []}
              labelFor={(v) => LEAD_KIND_LABEL[v as LeadKind] ?? v}
            />
            <FacetPanel
              title={t("crm.leads.agent")}
              paramKey="assignedTo"
              order="name"
              buckets={facets.assignedTo ?? []}
              labelFor={userName}
              nullLabel={t("crm.leads.unassigned")}
            />
            <FacetPanel
              title={t("common.fields.destination")}
              paramKey="destinationId"
              order="name"
              buckets={facets.destinationId ?? []}
              labelFor={destName}
              nullLabel="—"
            />
          </>
        }
      >
        <div className="card overflow-hidden p-0">
          {leads.length === 0 ? (
            <div className="flex h-48 flex-col items-center justify-center text-gray-500">
              <UserCircle className="mb-2 h-10 w-10 text-gray-300" />
              <p>{t("crm.leads.noLeads")}</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
                <tr>
                  {[
                    t("crm.leads.table.lead"),
                    t("crm.leads.table.contact"),
                    t("crm.leads.table.interest"),
                    t("crm.leads.table.pax"),
                    t("crm.leads.table.value"),
                    t("common.fields.status"),
                    t("crm.leads.table.agent"),
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {leads.map((lead) => {
                  const cfg = LEAD_STATUS_CONFIG[lead.status] ?? LEAD_STATUS_CONFIG.received;
                  return (
                    <tr key={lead.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-3">
                        <Link
                          to={`/leads/${lead.id}`}
                          className="text-primary-600 dark:text-primary-400 font-mono text-sm font-medium hover:underline"
                        >
                          {lead.leadNumber}
                        </Link>
                        <p className="text-xs text-gray-400">{formatDate(lead.createdAt)}</p>
                        <LeadKindBadge kind={lead.kind} className="mt-1" />
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900 dark:text-white">
                          {lead.contactName || "—"}
                        </p>
                        <p className="text-xs text-gray-500">
                          {lead.contactEmail || lead.contactPhone || ""}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {lead.tripTitle || lead.destinationName || "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                        {lead.paxAdults + lead.paxChildren}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                        {lead.estimatedValue ? formatPrice(lead.estimatedValue) : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`badge ${cfg.className}`}>{cfg.label}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                        {lead.assignedToName || (
                          <span className="text-gray-400">{t("crm.leads.unassigned")}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </FacetedListLayout>
    </div>
  );
}
